import type { TutorSubjectSwitch } from './studyTutorPanelLayout';
import './studyMobile.css';
import { ScienceTutorWorkspace } from './ScienceTutorWorkspace';
import type { ScienceTutorContext } from './scienceTutorContext';
import { ArrowLeft } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { StudyNavigation } from './StudyNavigation';
import { StudyTutorPanelSlot } from './StudyTutorPanelSlot';
import { StudyToolLearningSource } from './StudyToolLearningSource';

/** Where the phone back arrow goes, for example `Back to Mathematics`. */
export interface StudyMobileBack {
  to: string;
  label: string;
}

/**
 * A phone title always comes with its own back target (study-capture-fixes-prd.md F-01): each
 * page returns the learner to where they came from, never to one fixed page.
 */
type StudyMobileHeader =
  | { mobileTitle?: undefined; mobileBack?: undefined }
  | { mobileTitle: string; mobileBack: StudyMobileBack };

export function StudyShell({
  children,
  scienceTutor,
  tutorPanel,
  mobileLab = false,
  mobileTitle,
  mobileBack,
  compactContent = false,
}: {
  children: ReactNode;
  scienceTutor?: ScienceTutorContext;
  /**
   * Subject Space Tutor panel (study-subject-tutor-panel-prd.md §3.1). A page passes this OR
   * `scienceTutor`, never both: one Tutor per screen. Session and tool pages do not pass it.
   * An empty `subjectKey` shows the "set up your term" state, which calls `onSetUp`.
   */
  tutorPanel?: { subjectKey: string; subjectSwitch?: TutorSubjectSwitch; onSetUp?: () => void };
  mobileLab?: boolean;
  compactContent?: boolean;
} & StudyMobileHeader) {
  const { pathname } = useLocation();
  const [params] = useSearchParams();
  const subject = /^\/learn\/study\/subjects\/([^/]+)\/tools\/.+/.exec(pathname)?.[1];
  const subjects: Record<string, string> = {
    mathematics: 'Mathematics',
    chemistry: 'Chemistry',
    physics: 'Physics',
    biology: 'Biology',
    science: 'Science',
  };
  const label = subject ? subjects[subject] : undefined;
  const year = params.get('year');
  const suffix = year === 'all' || (year && /^(?:[6-9]|1[0-2])$/.test(year)) ? `?year=${year}` : '';

  return (
    <div
      className="study-shell relative flex h-full min-h-0 min-w-0 flex-col bg-canvas pt-[env(safe-area-inset-top)]"
      data-testid="study-portal"
      data-study-tool-subject={subject || undefined}
    >
      <header className="study-header shrink-0 border-b border-hairline bg-canvas-pure px-4 py-2">
        <div className="study-header-row mx-auto flex w-full max-w-[1440px] flex-wrap items-center justify-between gap-x-4">
          <Link
            to="/learn/study"
            aria-label="Airbotix Study home"
            className="flex min-h-11 shrink-0 items-center gap-3 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink"
          >
            <img src="/logo-black-horizontal-approved-20261021.png" alt="Airbotix" className="h-6 w-auto" />
            <span className="border-l border-hairline pl-3 text-sm font-extrabold text-ink">
              Study
            </span>
          </Link>
          <StudyNavigation />
          <div className="study-local-navigation min-w-0">
            <StudyNavigation mobile />
          </div>
        </div>
      </header>
      {/* Vertical-only overscroll containment: horizontal overscroll must still reach the page so the
          trackpad swipe back/forward keeps working (reported 2026-10-07). */}
      <main className="study-main mb-[calc(7rem+env(safe-area-inset-bottom))] min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-y-contain">
        <div
          data-study-content
          className={`study-content mx-auto box-border w-full min-w-0 max-w-[1440px] px-4 py-5 sm:px-6 xl:px-10 xl:py-9 pb-6 xl:pb-6 ${compactContent ? '!py-4' : ''} ${scienceTutor ? 'science-study-content' : ''} ${mobileLab ? 'study-mobile-lab' : ''}`}
        >
          {mobileTitle && mobileBack && (
            <div className="study-page-back mb-3 flex min-h-11 items-center gap-2 sm:hidden">
              <Link
                to={mobileBack.to}
                aria-label={mobileBack.label}
                className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-hairline"
              >
                <ArrowLeft className="h-5 w-5" aria-hidden="true" />
              </Link>
              <h1 className="text-base font-extrabold text-ink">{mobileTitle}</h1>
            </div>
          )}
          {subject && <StudyToolLearningSource />}
          {scienceTutor ? (
            <ScienceTutorWorkspace
              key={`${scienceTutor.subjectKey}:${scienceTutor.toolId}`}
              context={scienceTutor}
            >
              {children}
            </ScienceTutorWorkspace>
          ) : (
            children
          )}
          {label && (
            <footer
              role="contentinfo"
              aria-label={`${label} tool navigation`}
              className="study-tool-footer mt-8 grid grid-cols-1 gap-3 border-t border-hairline pt-5 text-sm font-bold text-slate2 sm:grid-cols-2"
              style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
            >
              <Link
                to={`/learn/study/subjects/${subject}/tools${suffix}`}
                className="flex min-h-11 items-center justify-center rounded-2xl border border-hairline bg-canvas-pure px-4 py-3 text-center hover:text-ink"
              >
                Back to {label} tools
              </Link>
              <Link
                to={`/learn/study/subjects/${subject}${suffix}`}
                className="flex min-h-11 items-center justify-center rounded-2xl border border-hairline bg-canvas-pure px-4 py-3 text-center hover:text-ink"
              >
                {label} overview
              </Link>
            </footer>
          )}
        </div>
      </main>
      {tutorPanel && !scienceTutor && (
        <StudyTutorPanelSlot
          subjectKey={tutorPanel.subjectKey}
          subjectSwitch={tutorPanel.subjectSwitch}
          onSetUp={tutorPanel.onSetUp}
        />
      )}
    </div>
  );
}
